// Helpers for talking to the WebAuthn browser API. The Ruby `webauthn` gem
// serializes challenges and credential IDs as base64url strings, but
// navigator.credentials wants/returns ArrayBuffers — so we convert at the edges.

function base64urlToBuffer(base64url) {
  const padding = "=".repeat((4 - (base64url.length % 4)) % 4)
  const base64 = (base64url + padding).replace(/-/g, "+").replace(/_/g, "/")
  const binary = atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return bytes.buffer
}

function bufferToBase64url(buffer) {
  const bytes = new Uint8Array(buffer)
  let binary = ""
  for (const byte of bytes) binary += String.fromCharCode(byte)
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "")
}

export function supportsWebAuthn() {
  return typeof window.PublicKeyCredential !== "undefined" &&
    typeof navigator.credentials?.create === "function"
}

// Register a new passkey from options issued by the server.
export async function createCredential(options) {
  options.challenge = base64urlToBuffer(options.challenge)
  options.user.id = base64urlToBuffer(options.user.id)
  if (options.excludeCredentials) {
    options.excludeCredentials = options.excludeCredentials.map((c) => ({
      ...c,
      id: base64urlToBuffer(c.id)
    }))
  }

  const credential = await navigator.credentials.create({ publicKey: options })
  return {
    type: credential.type,
    id: credential.id,
    rawId: bufferToBase64url(credential.rawId),
    response: {
      attestationObject: bufferToBase64url(credential.response.attestationObject),
      clientDataJSON: bufferToBase64url(credential.response.clientDataJSON)
    }
  }
}

// Assert an existing passkey (sign-in) from options issued by the server.
export async function getCredential(options) {
  options.challenge = base64urlToBuffer(options.challenge)
  if (options.allowCredentials) {
    options.allowCredentials = options.allowCredentials.map((c) => ({
      ...c,
      id: base64urlToBuffer(c.id)
    }))
  }

  const credential = await navigator.credentials.get({ publicKey: options })
  return {
    type: credential.type,
    id: credential.id,
    rawId: bufferToBase64url(credential.rawId),
    response: {
      authenticatorData: bufferToBase64url(credential.response.authenticatorData),
      clientDataJSON: bufferToBase64url(credential.response.clientDataJSON),
      signature: bufferToBase64url(credential.response.signature),
      userHandle: credential.response.userHandle
        ? bufferToBase64url(credential.response.userHandle)
        : null
    }
  }
}

// POST JSON to a same-origin endpoint, carrying the Rails CSRF token. Returns
// the parsed JSON body (endpoints in this app always respond with JSON).
export async function postJSON(url, body) {
  const token = document.querySelector("meta[name='csrf-token']")?.content
  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Accept": "application/json",
      "X-CSRF-Token": token
    },
    body: body == null ? null : JSON.stringify(body)
  })
  return response.json()
}
